import * as Yup from 'yup';

const validateAge = (birthDate, passengerType) => {
  if (!birthDate) return false;
  
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  switch (passengerType) {
    case 'ADT':
      return age >= 12;
    case 'CHD':
      return age >= 2 && age <= 11;
    case 'INF':
      return age < 2;
    default:
      return false;
  }
};

/**
 * Genera un esquema de validación dinámico para pasajeros basado en la configuración
 * @param {Object} passengerConfig - Configuración del pasajero proveniente de la API
 * @param {string} passengerType - Tipo de pasajero (ADT, CHD, INF, etc.)
 * @param {boolean} isAdult - Indica si el pasajero es adulto
 * @returns {Object} Esquema de validación Yup
 */
export const generatePassengerSchema = (passengerConfig, passengerType = 'ADT', isAdult = true) => {
  // Si no hay configuración, usamos el esquema por defecto
  if (!passengerConfig || !passengerConfig.passenger) {
    return getDefaultPassengerSchema(isAdult);
  }

  // Obtener los campos requeridos para este tipo de pasajero
  const requiredFields = passengerConfig.passenger.requiredFields[passengerType] || [];

  // Base del esquema con validaciones básicas
  const schema = {
    // El gender solo se valida para adultos
    gender: Yup.string().when('$isAdult', {
      is: true,
      then: (schema) => schema.oneOf(['Male', 'Female'], 'El género debe ser Male o Female').required('El género es requerido'),
      otherwise: (schema) => schema.optional(),
    }),

    // Nombre siempre tiene validación básica
    name: Yup.string().min(2, 'El nombre debe tener al menos 2 caracteres').required('El nombre es requerido'),

    // Apellido siempre tiene validación básica
    lastName: Yup.string()
      .min(2, 'Los apellidos deben tener al menos 2 caracteres')
      .max(41, 'Máximo 41 caracteres')
      .required('Los apellidos son requeridos'),

    // Pasajero frecuente
    frequentFlyer: Yup.string().optional(),

    // Fecha de nacimiento con validación de edad según tipo de pasajero
    birthDate: Yup.date()
      .required('La fecha de nacimiento es requerida')
      .test('age-validation', 'La edad no corresponde con el tipo de pasajero', function(value) {
        return validateAge(value, passengerType);
      }),

    // Tipo de documento
    documentType: Yup.string().required('El tipo de documento es requerido'),

    // Número de documento con validación condicional
    documentNumber: Yup.string().test(
      'validate-doc',
      'Formato de documento inválido',
      function (value) {
        const docType = this.parent.documentType;

        // Si no hay valor o no es requerido, no validamos
        if (!value && !requiredFields.includes('doc_number')) return true;

        if (docType === 'dni') {
          return /^\d{7,8}$/.test(value);
        }

        if (docType === 'pasaporte') {
          return /^[A-Z0-9]{6,12}$/.test(value?.toUpperCase() || '');
        }

        if (docType === 'cedula') {
          return /^\d{6,10}$/.test(value);
        }

        return true;
      },
    ),

    // Otros campos
    issueDate: Yup.date().optional(),
    issuedIn: Yup.string().optional(),
    nationality: Yup.string().optional(),
    expiryDate: Yup.date().optional(),

    // Campos especiales CTCL
    ctclType: Yup.string(),
    ctclNumber: Yup.string(),
  };

  // Añadir required a los campos según la configuración
  if (requiredFields.includes('name')) {
    schema.name = schema.name.required('El nombre es requerido');
  }

  if (requiredFields.includes('surname')) {
    schema.lastName = schema.lastName.required('Los apellidos son requeridos');
  }

  if (requiredFields.includes('birthdate')) {
    schema.birthDate = schema.birthDate.required('La fecha de nacimiento es requerida');
  }

  if (requiredFields.includes('doc_type')) {
    schema.documentType = schema.documentType.required('El tipo de documento es requerido');
  }

  if (requiredFields.includes('doc_number')) {
    schema.documentNumber = schema.documentNumber.required('El número de documento es requerido');
  }

  if (requiredFields.includes('frequent_traveller')) {
    schema.frequentFlyer = schema.frequentFlyer.required(
      'El número de viajero frecuente es requerido',
    );
  }

  if (requiredFields.includes('issue_date')) {
    schema.issueDate = schema.issueDate.required('La fecha de expedición es requerida');
  }

  if (requiredFields.includes('issued_in')) {
    schema.issuedIn = schema.issuedIn.required('El lugar de expedición es requerido');
  }

  if (requiredFields.includes('nationality')) {
    schema.nationality = schema.nationality.required('La nacionalidad es requerida');
  }

  if (requiredFields.includes('expiry_date')) {
    schema.expiryDate = schema.expiryDate.required('La fecha de caducidad es requerida');
  }

  if (requiredFields.includes('ctcl_type')) {
    schema.ctclType = schema.ctclType.required('El tipo de documento fiscal es requerido');
  }

  if (requiredFields.includes('ctcl_number')) {
    schema.ctclNumber = schema.ctclNumber.required('El número de documento fiscal es requerido');
  }

  return Yup.object(schema);
};

/**
 * Esquema predeterminado cuando no hay configuración disponible
 * @param {boolean} isAdult - Indica si el pasajero es adulto
 * @returns {Object} Esquema de validación Yup
 */
const getDefaultPassengerSchema = (isAdult = true) => {
  return Yup.object({
    gender: isAdult ? Yup.string().required('El género es requerido') : Yup.string(),
    name: Yup.string()
      .min(2, 'El nombre debe tener al menos 2 caracteres')
      .required('El nombre es requerido'),
    lastName: Yup.string()
      .min(2, 'Los apellidos deben tener al menos 2 caracteres')
      .max(41, 'Máximo 41 caracteres')
      .required('Los apellidos son requeridos'),
    frequentFlyer: Yup.string(),
    birthDate: Yup.date()
      .required('La fecha de nacimiento es requerida')
      .test('age-validation', 'La edad no corresponde con el tipo de pasajero', function(value) {
        return validateAge(value, isAdult ? 'ADT' : 'CHD');
      }),
    documentType: Yup.string().required('El tipo de documento es requerido'),
    documentNumber: Yup.string()
      .required('El número de documento es requerido')
      .test('validate-doc', 'Formato de documento inválido', function (value) {
        const docType = this.parent.documentType;

        if (!value) return false;

        if (docType === 'dni') {
          return /^\d{7,8}$/.test(value);
        }

        if (docType === 'pasaporte') {
          return /^[A-Z0-9]{6,12}$/.test(value.toUpperCase());
        }

        if (docType === 'cedula') {
          return /^\d{6,10}$/.test(value);
        }

        return true;
      }),
    issueDate: Yup.date(),
    issuedIn: Yup.string(),
    nationality: Yup.string(),
    expiryDate: Yup.date(),
  });
};

// Mantener los esquemas existentes para compatibilidad
export const passengerSchema = getDefaultPassengerSchema();

export const emergencyContactSchema = Yup.object().shape({
  name: Yup.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  lastName: Yup.string()
    .min(2, 'Los apellidos deben tener al menos 2 caracteres')
    .max(41, 'El número máximo de caracteres es de 41'),
  phone: Yup.object().shape({
    codigo: Yup.string().required('El código de país es requerido'),
    numero: Yup.string()
      .transform((value) => value?.replace(/\D/g, ''))
      .matches(/^[0-9]{8,}$/, 'Solo se permiten números'),
  }),
  country: Yup.string(),
});

export const fiscalDataSchema = Yup.object().shape({
  documentType: Yup.string().required('El tipo de documento fiscal es requerido'),
  documentNumber: Yup.string()
    .required('El número de documento fiscal es requerido')
    .when('documentType', {
      is: 'dni',
      then: (schema) =>
        schema
          .matches(/^\d{7,8}$/, 'El DNI debe tener entre 7 y 8 dígitos numéricos')
          .transform((value) => value?.replace(/\D/g, '')),
    })
    .when('documentType', {
      is: 'pasaporte',
      then: (schema) =>
        schema
          .matches(
            /^[A-Z0-9]{6,12}$/,
            'El pasaporte debe tener entre 6 y 12 caracteres alfanuméricos',
          )
          .transform((value) => value?.replace(/[^A-Z0-9]/gi, '').toUpperCase()),
    })
    .when('documentType', {
      is: 'cedula',
      then: (schema) =>
        schema
          .matches(/^\d{6,10}$/, 'La cédula debe tener entre 6 y 10 dígitos numéricos')
          .transform((value) => value?.replace(/\D/g, '')),
    }),
});

/**
 * Genera un esquema de validación dinámico basado en la configuración de contacto de la API
 * y manteniendo las reglas de validación existentes
 * @param {Object} contactConfig - Configuración de contacto de la API
 * @returns {Object} - Esquema de validación de Yup
 */
export const generateContactSchema = (contactConfig) => {
  const { fields = [], requiredFields = [], mandatory = false } = contactConfig || {};
  const schema = {};

  const fieldMapping = {
    name: 'nombre',
    surname: 'apellidos',
    email: 'email',
    phone: 'telefono1',
    phone2: 'telefono2',
  };

  const validationRules = {
    nombre: Yup.string().min(2, 'El nombre debe tener al menos 2 caracteres'),

    apellidos: Yup.string()
      .min(2, 'Los apellidos deben tener al menos 2 caracteres')
      .max(41, 'El número máximo de caracteres es de 41'),

    email: Yup.string().matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
      'Email inválido',
    ),

    telefono1: {
      codigo: Yup.string(),
      numero: Yup.string()
        .transform((value) => value?.replace(/\D/g, ''))
        .matches(/^[0-9]{8,}$/, 'El número ingresado no es valido'),
    },

    telefono2: {
      codigo: Yup.string(),
      numero: Yup.string()
        .transform((value) => value?.replace(/\D/g, ''))
        .matches(/^[0-9]{8,}$/, 'El número ingresado no es valido')
        .test('is-empty-or-valid', 'El número debe tener al menos 8 dígitos', function (value) {
          return !value || value.length >= 8;
        }),
    },
  };

  fields.forEach((field) => {
    const fieldName = fieldMapping[field.name];
    if (!fieldName) return;

    const isRequired = requiredFields.includes(field.name);

    if (fieldName === 'nombre') {
      schema[fieldName] = validationRules.nombre.clone();
      if (isRequired) {
        schema[fieldName] = schema[fieldName].required('El nombre es requerido');
      }
    } else if (fieldName === 'apellidos') {
      schema[fieldName] = validationRules.apellidos.clone();
      if (isRequired) {
        schema[fieldName] = schema[fieldName].required('Los apellidos son requeridos');
      }
    } else if (fieldName === 'email') {
      schema[fieldName] = validationRules.email.clone();
      if (isRequired) {
        schema[fieldName] = schema[fieldName].required('El email es requerido');
      }
    } else if (fieldName === 'telefono1') {
      if (isRequired) {
        schema[fieldName] = Yup.object({
          codigo: validationRules.telefono1.codigo.required('El código de país es requerido'),
          numero: validationRules.telefono1.numero.required('El número es requerido'),
        });
      } else {
        schema[fieldName] = Yup.object({
          codigo: validationRules.telefono1.codigo,
          numero: validationRules.telefono1.numero,
        });
      }
    } else if (fieldName === 'telefono2') {
      schema[fieldName] = Yup.object({
        codigo: validationRules.telefono2.codigo,
        numero: validationRules.telefono2.numero,
      });
    }
  });

  return Yup.object().shape(schema);
};

/**
 * Genera los valores iniciales del formulario con campos fijos y solo phone2 opcional
 * @param {Object} contactConfig - Configuración de contacto de la API
 * @returns {Object} - Valores iniciales del formulario
 */
export const generateInitialValues = (contactConfig) => {
  const fields = contactConfig?.fields || [];
  const hasPhone2 = fields.some((field) => field.name === 'phone2');

  return {
    nombre: '',
    apellidos: '',
    email: '',
    telefono1: {
      numero: '',
    },
    ...(hasPhone2 && {
      telefono2: {
        numero: '',
      },
    }),
  };
};
